import React, { useState, forwardRef } from 'react';
import { Heart, Send, CheckCircle, AlertCircle, Mail, User, Building, Calendar, MessageSquare } from 'lucide-react';
import InputField from '../../../shared/components/ui/forms/InputField';
import PrimaryButton from '../../../shared/components/ui/buttons/PrimaryButton';
import { apiClient, ApiError, NetworkError } from '../../../shared/utils/api/api';

interface ContactFormData {
  name: string;
  email: string;
  venue: string;
  weddingDate: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  venue?: string;
  weddingDate?: string;
  message?: string;
}

interface ContactResponse {
  success: boolean;
  message: string;
  contactId?: number;
  timestamp?: string;
  errors?: { [key: string]: string };
}

const ContactForm = forwardRef<HTMLDivElement>((props, ref) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    venue: '',
    weddingDate: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // 名前の検証
    if (!formData.name.trim()) {
      newErrors.name = 'お名前は必須項目です';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'お名前は2文字以上で入力してください';
    }

    // メールアドレスの検証
    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスは必須項目です';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = '正しいメールアドレスを入力してください';
      }
    }

    // 式場名の検証（任意）
    if (formData.venue.trim() && formData.venue.trim().length < 2) {
      newErrors.venue = '式場名は2文字以上で入力してください';
    }

    // 挙式予定日の検証（任意だが、入力がある場合は未来日をチェック）
    if (formData.weddingDate) {
      const selectedDate = new Date(formData.weddingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.weddingDate = '挙式予定日は今日以降の日付を選択してください';
      }
    }
    

    // お問い合わせ内容の検証
    if (!formData.message.trim()) {
      newErrors.message = 'お問い合わせ内容は必須項目です';
    } 

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ContactFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));

    // エラーがある場合はリアルタイムでクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleClick = () => {  
    window.open(
    'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2jUW51cRbgRVXkXM2xCtLRnDqsy9_aR_ATY8aSC4z7DpRQODMNlbM8-A_Tjy5bJKQwNCazqoI7',
    '_blank'
  );
  setIsSubmitted(false)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      console.log('問い合わせフォーム送信データ:', formData);
      
      // 実際のAPI呼び出し
      const response = await apiClient.post<ContactResponse>('/api/contact', formData);

      if (response.success) {
        console.log('問い合わせ送信成功:', response.contactId);
        setIsSubmitted(true);
        
        // フォームデータをリセット
        setFormData({
          name: '',
          email: '',
          venue: '',
          weddingDate: '',
          message: ''
        });
      } else {
        throw new Error(response.message || '送信に失敗しました');
      }

    } catch (error) {
      console.error('送信エラー:', error);
      
      if (error instanceof ApiError) {
        if (error.status === 400) {
          setSubmitError('入力内容をご確認ください。');
        } else if (error.status >= 500) {
          setSubmitError('サーバーエラーが発生しました。しばらくしてから再度お試しください。');
        } else {
          setSubmitError('送信に失敗しました。');
        }
      } else if (error instanceof NetworkError) {
        setSubmitError('ネットワークエラーが発生しました。インターネット接続をご確認ください。');
      } else {
        setSubmitError('送信中にエラーが発生しました。しばらくしてから再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section ref={ref} className="contact-form-section">
        <div className="contact-form-container">
          <div className="success-message">
            <CheckCircle className="success-icon" size={64} />
            <h2 className="success-title">お問い合わせありがとうございます</h2>
            <p className="success-description">
              お問い合わせを承りました。担当者より2営業日以内にご連絡いたします。
              <br />
              また、担当者との日程調整は下のボタンから
            </p>
            <button 
              className="cta-button secondary"
              onClick={handleClick}
            >
              担当者のスケジュールを予約
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="contact-form-section">
      <div className="section-header">
        <div className="title-decoration">
          <div className="decoration-line"></div>
          <Heart className="section-heart" />
          <div className="decoration-line"></div>
        </div>
        <p className="contact-form-subtitle">
          Photo Memorial の導入に関するご質問・ご相談はお気軽にお問い合わせください
        </p>
      </div>

      <div className="contact-form-container">
        <div className="contact-form-content">

          <div className="contact-form">
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <User size={16} />
                  お名前 <span className="required">*</span>
                </label>
                <InputField
                  id="name"
                  type="text"
                  placeholder="例: 田中 太郎"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  className={`input ${errors.name ? 'error' : ''}`}
                />
                {errors.name && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <Mail size={16} />
                  メールアドレス <span className="required">*</span>
                </label>
                <InputField
                  id="email"
                  type="email"
                  placeholder="例: tanaka@example.com"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  className={`input ${errors.email ? 'error' : ''}`}
                />
                {errors.email && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.email}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="venue" className="form-label">
                  <Building size={16} />
                  式場名・会社名
                </label>
                <InputField
                  id="venue"
                  type="text"
                  placeholder="例: ○○ホテル、○○ウェディング"
                  value={formData.venue}
                  onChange={handleInputChange('venue')}
                  className={`input ${errors.venue ? 'error' : ''}`}
                />
                {errors.venue && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.venue}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="weddingDate" className="form-label">
                  <Calendar size={16} />
                  挙式予定日
                </label>
                <InputField
                  id="weddingDate"
                  type="date"
                  value={formData.weddingDate}
                  onChange={handleInputChange('weddingDate')}
                  className={`input ${errors.weddingDate ? 'error' : ''}`}
                />
                {errors.weddingDate && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.weddingDate}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  <MessageSquare size={16} />
                  お問い合わせ内容 <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  placeholder="Photo Memorial についてご質問やご相談がございましたら、お気軽にお聞かせください。&#10;例：&#10;・導入を検討しているが、詳細な機能について教えてほしい&#10;・デモンストレーションを希望する"
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  className={`textarea ${errors.message ? 'error' : ''}`}
                  rows={6}
                />
                {errors.message && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.message}
                  </div>
                )}
              </div>

              {submitError && (
                <div className="submit-error">
                  <AlertCircle size={16} />
                  {submitError}
                </div>
              )}

              <div className="form-submit">
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting}
                  className={`cta-button primary large ${isSubmitting ? 'disabled' : ''}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      送信中...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      お問い合わせ送信
                    </>
                  )}
                </PrimaryButton>
              </div>

              <div className="form-footer">
                <p className="privacy-note">
                  ※ お客様からいただいた個人情報は、お問い合わせへの回答および
                  Photo Memorial に関するご案内のみに使用し、適切に管理いたします。
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

ContactForm.displayName = 'ContactForm';

export default ContactForm;